import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Cpu, Mail, Lock, CheckCircle, ArrowLeft, AlertTriangle } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageSelector from '../components/LanguageSelector';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useLanguage();

  if (token) {
    return <ResetForm token={token} navigate={navigate} t={t} />;
  }
  return <RequestForm navigate={navigate} t={t} />;
};

const RequestForm = ({ navigate, t }) => {
  const [email, setEmail] = useState('');
  const [userType, setUserType] = useState('client');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email) { setError(t('reset.enterEmail')); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/forgot-password`, { email, user_type: userType });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('reset.error'));
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="grid-pattern"></div>
        <div className="glass p-8 w-full max-w-md fade-in text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-white">{t('reset.emailSent')}</h2>
          <p className="text-gray-400 text-sm" data-testid="reset-email-sent">
            {t('reset.emailSentMsg')}
          </p>
          <p className="text-gray-500 text-xs">{t('reset.linkValid')}</p>
          <button onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2.5 bg-white/10 hover:bg-white/20 text-white rounded-xl text-sm font-medium transition-colors"
            data-testid="reset-back-login">
            {t('reset.backLogin')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="grid-pattern"></div>
      <div className="absolute top-4 right-4 z-30"><LanguageSelector /></div>
      <div className="glass p-8 w-full max-w-md fade-in">
        <Link to="/login" className="inline-flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors text-sm">
          <ArrowLeft className="w-4 h-4" /> {t('common.back')}
        </Link>

        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 mb-3">
            <Cpu className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">{t('reset.forgotTitle')}</h1>
          <p className="text-gray-400 text-sm mt-1">{t('reset.forgotDesc')}</p>
        </div>

        <div className="flex mb-5 bg-black/30 rounded-xl p-1">
          <button type="button" onClick={() => setUserType('client')}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${userType === 'client' ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' : 'text-gray-400 hover:text-white'}`}
            data-testid="reset-type-client">
            {t('common.client')}
          </button>
          <button type="button" onClick={() => setUserType('b2b')}
            className={`flex-1 py-2 px-3 rounded-lg text-xs font-semibold transition-all ${userType === 'b2b' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30' : 'text-gray-400 hover:text-white'}`}
            data-testid="reset-type-b2b">
            {t('landing.businessSpace')}
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center" data-testid="reset-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">
              {userType === 'client' ? t('reset.clientEmail') : t('login.proEmail')}
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input type="email" value={email} onChange={e => setEmail(e.target.value)}
                className="input-dark w-full pl-10 pr-4 py-3"
                placeholder={userType === 'client' ? 'votre@email.com' : 'contact@entreprise.com'}
                required autoFocus data-testid="reset-email-input" />
            </div>
          </div>
          <button type="submit" disabled={loading}
            className={`w-full py-3 rounded-xl font-bold text-white transition-all ${userType === 'b2b' ? 'bg-purple-600 hover:bg-purple-700' : 'btn-cyan'} ${loading ? 'opacity-50' : ''}`}
            data-testid="reset-submit">
            {loading ? t('reset.sending') : t('reset.sendLink')}
          </button>
        </form>
      </div>
    </div>
  );
};

const ResetForm = ({ token, navigate, t }) => {
  const [password, setPassword] = useState('');
  const [password2, setPassword2] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (password.length < 4) { setError(t('reset.pwdMinLength')); return; }
    if (password !== password2) { setError(t('reset.pwdNoMatch')); return; }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/reset-password`, { token, new_password: password });
      setSuccess(true);
    } catch (err) {
      setError(err.response?.data?.detail || t('reset.error'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="grid-pattern"></div>
        <div className="glass p-8 w-full max-w-md fade-in text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h2 className="text-xl font-bold text-white">{t('reset.pwdReset')}</h2>
          <p className="text-gray-400 text-sm" data-testid="reset-success">
            {t('reset.pwdResetMsg')}
          </p>
          <button onClick={() => navigate('/login')}
            className="mt-4 px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-bold transition-colors"
            data-testid="reset-go-login">
            {t('login.connect')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="grid-pattern"></div>
      <div className="glass p-8 w-full max-w-md fade-in">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br from-red-500 to-red-700 mb-3">
            <Cpu className="w-7 h-7 text-white" />
          </div>
          <h1 className="text-xl font-bold text-white">{t('reset.newPwdTitle')}</h1>
          <p className="text-gray-400 text-sm mt-1">{t('reset.newPwdDesc')}</p>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm text-center" data-testid="reset-form-error">
            <AlertTriangle className="w-4 h-4 inline mr-1" />{error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('reset.newPwd')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                className="input-dark w-full pl-10 pr-4 py-3" placeholder="Min. 4"
                required data-testid="reset-new-password" />
            </div>
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">{t('reset.confirmPwd')}</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <input type="password" value={password2} onChange={e => setPassword2(e.target.value)}
                className="input-dark w-full pl-10 pr-4 py-3" placeholder="..."
                required data-testid="reset-confirm-password" />
            </div>
          </div>
          <button type="submit" disabled={loading}
            className={`w-full py-3 rounded-xl font-bold text-white bg-green-600 hover:bg-green-700 transition-all ${loading ? 'opacity-50' : ''}`}
            data-testid="reset-password-submit">
            {loading ? t('reset.resetting') : t('reset.resetPwd')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
